import * as React from 'react'
import { Formik, FormikProps, getIn } from 'formik'
import { debounce } from 'lodash'
import {
  faFileAlt,
  faInfo,
  faPaperclip,
  faPenAlt
} from '@fortawesome/pro-light-svg-icons'

import {
  View,
  Section,
  StatusBorder
} from '@espoon-voltti/react-components/layout'
import {
  Button,
  Buttons,
  Text,
  ValidationMessage
} from '@espoon-voltti/react-components/elements'
import { Collapsible } from '@espoon-voltti/react-voltti/components/collapsible'

import { Org } from '@espoon-voltti/nuora-domain/applicants'
import { GrantPeriod } from '@espoon-voltti/nuora-domain/grants'

import { GlobalModal } from '@espoon-voltti/nuora-app-common/globals'

import { Breadcrumb } from '../../../../components'
import { t } from '../../../../i18n'
import { GeneralGrantDraftApp } from '../../../../domain'
import { localizedPathnames } from '../../../../utils/localized-pathname'

import { Attachments } from '../attachments'
import { OrgInfoWithEdit } from '../../partials/org-info'
import { SectionInfo } from '../../styles'
import { getAppErrors } from '../../validation/general-grant'
import { GeneralGrantFormData } from './form-data'
import { GeneralGrantFormPreviousYear } from './previous-year-finances'
import { NuoraOrgInfo } from '../nuora-org-info'
import { today } from '@espoon-voltti/ts-libs/lib/time'
import { setModalFocus } from '../../../../utils/modal-focus'

interface AppFormProps {
  app: GeneralGrantDraftApp
  org: Org
  grantPeriod: GrantPeriod
  onChange: (app: GeneralGrantDraftApp) => unknown
  onClose: () => unknown
  onDelete: () => unknown
  onSubmit: (app: GeneralGrantDraftApp) => unknown
}
export const GeneralGrantAppForm: React.FC<AppFormProps> = React.memo(
  (p) => {
    const handleSubmit = (app: GeneralGrantDraftApp) => {
      saveDebounced.cancel()
      p.onSubmit(app)
    }

    const saveAndClose = () => {
      saveDebounced.flush()
      p.onClose()
    }

    const lastYearDt = p.grantPeriod.startDatetime.plusYears(-1).endOfYear()
    const lastYearDay = lastYearDt.toLocalDate().format()
    const lastYear = lastYearDt.toLocalDate().d.year()
    const thisYear = today().d.year()

    return (
      <View
        dataQa="appForm"
        title={t(['views', 'form', 'newApplication'])}
        subtitle={p.org.name}
        status={t(['common', 'statuses', p.app.state])}
        breadcrumbs={
          <Breadcrumb
            route={[
              {
                label: t(['breadcrumb', 'home']),
                link: localizedPathnames.home(p.org.id)
              },
              { label: t(['breadcrumb', p.grantPeriod.type]), link: '#' }
            ]}
          />
        }
      >
        <Formik
          onSubmit={handleSubmit}
          initialValues={{
            ...p.app
          }}
          validate={getAppErrors}
        >
          {(form) => (
            <>
              <StatusBorder status={'new'} />
              {form.dirty ? saveDebounced(form.values, p.onChange) : ''}
              <Section>
                <Collapsible
                  dataQa={'orgInfo'}
                  title={t(['org', 'basicInfo', 'label'])}
                  icon={faInfo}
                  open={true}
                >
                  <SectionInfo>{t(['views', 'form', 'orgInfo'])}</SectionInfo>
                  <OrgInfoWithEdit org={p.org} />
                  <NuoraOrgInfo />
                </Collapsible>
              </Section>

              <Section>
                <Collapsible
                  dataQa={'applicationDetails'}
                  title={t([
                    'views',
                    'generalGrant',
                    'form',
                    'appDetails',
                    'title'
                  ])}
                  icon={faFileAlt}
                  open={true}
                >
                  <SectionInfo>
                    {t(['views', 'form', 'appDetails', 'text'])}
                  </SectionInfo>

                  <GeneralGrantFormData />
                </Collapsible>
              </Section>
              <Section>
                <Collapsible
                  dataQa={'previousYearFinances'}
                  title={t([
                    'views',
                    'generalGrant',
                    'form',
                    'previousYear',
                    'title'
                  ])}
                  icon={faFileAlt}
                  open={true}
                >
                  <GeneralGrantFormPreviousYear lastYearDay={lastYearDay} />
                </Collapsible>
              </Section>

              <Section>
                <Collapsible
                  dataQa={'attachments'}
                  title={t(['views', 'form', 'attachments', 'title'])}
                  icon={faPaperclip}
                  open={true}
                >
                  <SectionInfo>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'title'
                        ],
                        { year: lastYear }
                      )}
                    </p>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'planForYear'
                        ],
                        { year: thisYear }
                      )}
                    </p>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'budgetForYear'
                        ],
                        { year: thisYear }
                      )}
                    </p>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'minutesOfAnnualMeeting'
                        ],
                        { year: thisYear }
                      )}
                    </p>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'annualReport'
                        ],
                        { year: lastYear }
                      )}
                    </p>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'financialStatemment'
                        ],
                        { year: lastYear }
                      )}
                    </p>
                    <p>
                      {t(
                        [
                          'views',
                          'generalGrant',
                          'form',
                          'attachments',
                          'mandatoryAttachments',
                          'auditForAYear'
                        ],
                        { year: lastYear }
                      )}
                    </p>
                  </SectionInfo>
                  <Attachments />
                  <Text
                    align={'centered'}
                    display={'block'}
                    dataQa={'validationError'}
                  >
                    {getIn(form.errors, 'form.attachments') &&
                    getIn(form.touched, 'form.attachments') ? (
                      <ValidationMessage
                        state={'warning'}
                        message={t(['validations', 'attachmentsRequired'], {
                          min: 1
                        })}
                      />
                    ) : null}
                  </Text>
                </Collapsible>
              </Section>

              <Section>
                <Collapsible
                  dataQa={'signing'}
                  title={t([
                    'views',
                    'generalGrant',
                    'form',
                    'signing',
                    'title'
                  ])}
                  icon={faPenAlt}
                  open={true}
                >
                  <p>
                    {t(['views', 'generalGrant', 'form', 'signing', 'info'])}
                  </p>
                </Collapsible>
              </Section>
              <Buttons centered>
                <Button width={'wide'} onClick={p.onDelete} dataQa="delete">
                  {t(['views', 'form', 'delete', 'action'])}
                </Button>
                <Button
                  width={'wide'}
                  onClick={saveAndClose}
                  dataQa="saveAndClose"
                >
                  {t(['views', 'form', 'saveAndClose'])}
                </Button>
                <Button
                  width={'wide'}
                  dataQa={'sign'}
                  primary
                  onClick={validateAndSubmit(form)}
                  type={'submit'}
                >
                  {t(['views', 'form', 'toSigning'])}
                </Button>
              </Buttons>
            </>
          )}
        </Formik>
      </View>
    )
  },
  () => true /* ignore prop updates and let formik manage the form */
)

// this must return void, or render template must be changed accordingly, otherwise
// produces 'objects are not valid react...etc' error (tries to render promise)
const save = (
  data: GeneralGrantDraftApp,
  onChange: AppFormProps['onChange']
): void => {
  onChange(data)
}
const saveDebounced = debounce(save, 1000)

const validateAndSubmit = (formik: FormikProps<GeneralGrantDraftApp>) => () => {
  formik.submitForm()

  // NOTE: formik.submitForm() also runs validation but does it asynchronously
  // behind the scenes. This seems to be fixed in formik 2.x where submitForm
  // returns a Promise instead of void.
  // As a workaround we currently run validation here again to get input
  // whether or not to show validation errors modal.
  if (getAppErrors(formik.values) != null) {
    GlobalModal.get().show({
      dataQa: 'validationErrors',
      title: t(['common', 'modal', 'validationErrors', 'title'])
    })
    setModalFocus()
  }
}
